import { CheckCircle } from 'lucide-react';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Image19 from '../../Assets/20seaterbus/Gemini_Generated_Image_at0fl0at0fl0at0f.png';
import Image20 from '../../Assets/20seaterbus/20-seater-mini-bus-tempo.jpg';
import Image21 from '../../Assets/20seaterbus/Gemini_Generated_Image_weg9prweg9prweg9.png';
import { 
  Bus, 
  Users, 
  Wind, 
  Music, 
  Phone, 
  Mail, 
  MapPin, 
  Shield,
  Clock,
  ArrowLeft,
  Star,
  Luggage,
  Settings,
  Thermometer,
  Tv,
  Wifi
} from 'lucide-react';

const Bus20SeaterBangalore = () => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [showBookingForm, setShowBookingForm] = React.useState(false);
  
  const images = [Image19, Image20, Image21];
  
  const vehicleSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "20 Seater Mini Bus Rental Bangalore | AC Bus Hire - Nava Cabs",
    "description": "Book 20-seater AC mini bus in Bangalore. Central AC, reclining seats, entertainment. Perfect for school trips, corporate transport & group tours.",
    "model": "20 Seater Mini Bus",
    "vehicleTransmission": "Manual",
    "numberOfSeats": "20",
    "amenityFeature": "Air Conditioning, Entertainment System"
  };

  const features = [
    'Central Air Conditioning',
    'Reclining Comfort Seats',
    'Premium Audio System',
    'Experienced Professional Driver',
    'Safety Equipment & First Aid',
    'Large Panoramic Windows',
    'Overhead Storage Compartments',
    'USB Charging Ports',
    'Reading Lights',
    'Emergency Exit Windows',
    'Fire Extinguisher',
    'GPS Tracking System'
  ];

  const specifications = {
    'Vehicle Type': 'Mini Bus / Mini Coach',
    'Seating Capacity': '20 Passengers',
    'Engine': 'Heavy Duty Diesel',
    'Transmission': 'Manual',
    'Luggage Capacity': '15-18 Large Bags',
    'AC': 'Central Air Conditioning',
    'Fuel Type': 'Diesel',
    'Fuel Efficiency': '8-10 km/l',
    'Entertainment': 'Premium Audio System',
    'Safety': 'Emergency Exits, First Aid'
  };

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => prev === 0 ? images.length - 1 : prev - 1);

  if (showBookingForm) {
    return <BookingForm vehicle={{
      name: '20 Seater Mini Bus',
      seating: 20,
      priceRange: '₹40/km'
    }} onBack={() => setShowBookingForm(false)} />;
  }

  return (
    <>
      <Helmet>
        <title>20 Seater Mini Bus Rental Bangalore | AC Bus Hire @ ₹40/km - Nava Cabs</title>
        <meta 
          name="description" 
          content="✅ Book 20-seater AC mini bus in Bangalore. Central AC, reclining seats, entertainment system. Perfect for school trips, corporate transport, wedding events & group tours."
        />
        <meta name="keywords" content="20 seater bus rental bangalore, mini bus hire, school bus rental, corporate transport bus, wedding bus rental, group tour bus bangalore" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://navacabs.com/20-seater-bus-rental-bangalore" />
        <script type="application/ld+json">{JSON.stringify(vehicleSchema)}</script>
      </Helmet>
      
      <div className="min-h-screen bg-gray-50">
        {/* Back Button */}
        <div className="bg-white shadow-sm">
          <div className="container mx-auto px-4 py-2">
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-blue-600 transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </button>
          </div>
        </div>

        {/* Hero Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-8">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-4xl font-bold mb-3">
              20 Seater AC Mini Bus Rental in Bangalore
            </h1>
            <p className="text-lg opacity-90 max-w-2xl mx-auto">
              Premium 20-seater AC mini bus hire for school trips, corporate transport, 
              wedding events, and group tours from Bangalore.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <div className="bg-white/20 px-3 py-1.5 rounded-full flex items-center text-sm">
                <Users className="w-4 h-4 mr-1.5" /> 20 Seater
              </div>
              <div className="bg-white/20 px-3 py-1.5 rounded-full flex items-center text-sm">
                <Thermometer className="w-4 h-4 mr-1.5" /> Central AC
              </div>
              <div className="bg-white/20 px-3 py-1.5 rounded-full flex items-center text-sm">
                ₹40/km
              </div>
              <div className="bg-white/20 px-3 py-1.5 rounded-full flex items-center text-sm">
                <Bus className="w-4 h-4 mr-1.5" /> Mini Coach
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Image Gallery */}
              <div className="bg-white rounded-lg shadow p-3">
                <div className="relative rounded-md overflow-hidden">
                  <img 
                    src={images[currentImageIndex]} 
                    alt="20 Seater AC Mini Bus interior and exterior - Nava Cabs Bangalore"
                    className="w-full h-56 md:h-64 object-cover"
                  />
                  <button 
                    onClick={prevImage}
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 p-1.5 rounded-full hover:bg-white transition-colors"
                  >
                    ←
                  </button>
                  <button 
                    onClick={nextImage}
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 p-1.5 rounded-full hover:bg-white transition-colors"
                  >
                    →
                  </button>
                  <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-1">
                    {images.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-2 h-2 rounded-full ${index === currentImageIndex ? 'bg-white' : 'bg-white/50'}`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Mobile Booking Section */}
              <div className="lg:hidden mt-6 bg-white rounded-lg shadow p-4">
                <div className="text-center mb-4">
                  <div className="text-3xl font-bold text-blue-600 mb-1">₹40/km</div>
                  <div className="text-gray-600 text-sm">Starting Price</div>
                  <div className="text-xs text-gray-500 mt-1">*Exclusive of GST & tolls</div>
                </div>
                <button 
                  onClick={() => setShowBookingForm(true)}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-lg font-bold text-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl mb-4"
                >
                  Book 20 Seater Bus Now
                </button>
                
                {/* Quick Contact on Mobile */}
                <div className="space-y-3">
                  <a 
                    href="tel:+919964758999" 
                    className="flex items-center justify-center gap-3 bg-green-50 text-green-800 p-3 rounded-lg hover:bg-green-100 transition-colors"
                  >
                    <Phone className="w-5 h-5" />
                    <div className="text-left">
                      <div className="font-bold">+91 99647 58999</div>
                      <div className="text-xs">Call for Instant Booking</div>
                    </div>
                  </a>
                  <a 
                    href="https://wa.me/919964758999" 
                    className="flex items-center justify-center gap-3 bg-green-600 text-white p-3 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <div className="font-bold">WhatsApp Instant Booking</div>
                  </a>
                </div>
              </div>

              {/* SEO Content */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  20 Seater Mini Bus Rental Service in Bangalore
                </h2>
                
                <div className="text-gray-700 space-y-3 text-sm">
                  <p>
                    <strong>Nava Cabs offers premium 20-seater mini bus rental services in Bangalore</strong> 
                    for all your group transportation needs. Our fully air-conditioned mini buses are perfect for 
                    <strong> school educational trips, corporate employee transport, wedding guest transportation, 
                    college excursions, and tourist group tours</strong>.
                  </p>
                  
                  <p>
                    Featuring <strong>20 comfortable reclining seats with individual armrests</strong>, our mini buses ensure 
                    a pleasant journey for medium-sized groups. The vehicles are equipped with 
                    <strong> premium audio system, USB charging ports, overhead storage, and large panoramic windows</strong> 
                    for scenic views.
                  </p>
                  
                  <h3 className="text-xl font-bold mt-6 mb-3">Ideal for Educational & Corporate Events</h3>
                  <p>
                    Our 20-seater mini bus is perfect for <strong>school trips to Mysore Zoo, Wonderla, Bannerghatta 
                    Biological Park, Ramanagara, and Nandi Hills</strong>. Also suitable for 
                    <strong> corporate team building events, factory visits, and client transportation</strong>.
                  </p>
                  
                  <h3 className="text-xl font-bold mt-6 mb-3">Service Coverage in Bangalore</h3>
                  <p>
                    We provide <strong>mini bus rental services throughout Bangalore</strong> covering all areas 
                    including Whitefield, Electronic City, Koramangala, Indiranagar, HSR Layout, 
                    JP Nagar, Banashankari, Rajajinagar, Malleshwaram, Yeshwanthpur, and Airport zone.
                  </p>
                  
                  <h3 className="text-xl font-bold mt-6 mb-3">Why Choose Our 20 Seater Bus Service?</h3>
                  <ul className="grid md:grid-cols-2 gap-2">
                    <li className="flex items-center text-sm"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> 24/7 Availability</li>
                    <li className="flex items-center text-sm"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> Experienced Drivers</li>
                    <li className="flex items-center text-sm"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> GPS Tracking</li>
                    <li className="flex items-center text-sm"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> Fixed Package Rates</li>
                    <li className="flex items-center text-sm"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> Regular Maintenance</li>
                    <li className="flex items-center text-sm"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> Full Insurance</li>
                    <li className="flex items-center text-sm"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> Child Safety Features</li>
                    <li className="flex items-center text-sm"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> Emergency Equipment</li>
                  </ul>
                </div>
              </div>

              {/* Features */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Mini Bus Features & Amenities</h2>
                <div className="grid md:grid-cols-2 gap-3">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-center p-2.5 bg-blue-50 rounded-md text-sm hover:bg-blue-100 transition-colors">
                      <CheckCircle className="w-4 h-4 text-blue-600 mr-2.5 flex-shrink-0" />
                      <span className="text-gray-800">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Specifications */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Technical Specifications</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.entries(specifications).map(([key, value]) => (
                    <div key={key} className="bg-gray-50 p-3 rounded-md hover:bg-gray-100 transition-colors">
                      <div className="flex items-center text-xs text-gray-600 font-medium mb-1">
                        <Settings className="w-3 h-3 mr-1" />
                        {key}
                      </div>
                      <div className="text-base font-semibold text-gray-900">{value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Ideal For Section */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                  <Users className="w-6 h-6 mr-2 text-blue-600" />
                  Ideal For
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="font-bold text-gray-800 mb-1">School Trips</div>
                    <div className="text-sm text-gray-600">Educational excursions & field trips</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="font-bold text-gray-800 mb-1">Corporate Transport</div>
                    <div className="text-sm text-gray-600">Employee shuttle & client transport</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="font-bold text-gray-800 mb-1">Wedding Events</div>
                    <div className="text-sm text-gray-600">Guest transportation</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="font-bold text-gray-800 mb-1">College Tours</div>
                    <div className="text-sm text-gray-600">Educational tours & sports events</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="font-bold text-gray-800 mb-1">Family Reunions</div>
                    <div className="text-sm text-gray-600">Large family gatherings</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="font-bold text-gray-800 mb-1">Group Tours</div>
                    <div className="text-sm text-gray-600">Tourist groups & sightseeing</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="hidden lg:block lg:col-span-1">
              <div className="bg-white rounded-lg shadow p-5 sticky top-20">
                <div className="text-center mb-5">
                  <div className="text-3xl font-bold text-blue-600 mb-1">₹40/km</div>
                  <div className="text-gray-600 text-sm">Starting Price</div>
                  <div className="text-xs text-gray-500 mt-1">*Exclusive of GST & tolls</div>
                </div>

                <button 
                  onClick={() => setShowBookingForm(true)}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-lg font-bold text-base mb-5 hover:from-blue-700 hover:to-blue-800 transition-all shadow hover:shadow-md transform hover:-translate-y-0.5"
                >
                  Book 20 Seater Bus Now
                </button>

                <div className="space-y-3">
                  <a 
                    href="tel:+919964758999" 
                    className="flex items-center justify-center gap-3 bg-green-50 text-green-800 p-3 rounded-lg hover:bg-green-100 transition-colors"
                  >
                    <Phone className="w-5 h-5" />
                    <div className="text-left">
                      <div className="font-bold">+91 99647 58999</div>
                      <div className="text-xs">Call for Instant Booking</div>
                    </div>
                  </a>

                  <div className="border-t pt-4">
                    <h3 className="font-bold text-gray-900 mb-3">Quick Enquiry</h3>
                    <div className="space-y-2">
                      <a 
                        href="https://wa.me/919964758999" 
                        className="block bg-green-600 text-white p-2.5 rounded-md text-center hover:bg-green-700 transition-colors text-sm"
                      >
                        WhatsApp: +91 99647 58999
                      </a>
                      <a 
                        href="mailto:info@navacabs.com" 
                        className="block bg-blue-600 text-white p-2.5 rounded-md text-center hover:bg-blue-700 transition-colors text-sm"
                      >
                        Email: info@navacabs.com
                      </a>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h3 className="font-bold text-gray-900 mb-3">Service Highlights</h3>
                    <div className="space-y-2.5">
                      <div className="flex items-center gap-2.5">
                        <Thermometer className="w-4 h-4 text-blue-600" />
                        <div>
                          <div className="font-semibold text-sm">Central AC</div>
                          <div className="text-xs text-gray-600">Full air conditioning</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <Shield className="w-4 h-4 text-green-600" />
                        <div>
                          <div className="font-semibold text-sm">Insured Vehicles</div>
                          <div className="text-xs text-gray-600">Comprehensive insurance</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <Star className="w-4 h-4 text-yellow-500" />
                        <div>
                          <div className="font-semibold text-sm">4.8/5 Rating</div>
                          <div className="text-xs text-gray-600">300+ school trips</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <MapPin className="w-4 h-4 text-blue-600" />
                        <div>
                          <div className="font-semibold text-sm">All Bangalore</div>
                          <div className="text-xs text-gray-600">Wide service coverage</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <Clock className="w-4 h-4 text-blue-600" />
                        <div>
                          <div className="font-semibold text-sm">24/7 Service</div>
                          <div className="text-xs text-gray-600">Round the clock availability</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h3 className="font-bold text-gray-900 mb-3">Price Includes</h3>
                    <div className="space-y-1.5">
                      <div className="flex items-center text-sm">
                        <CheckCircle className="w-3.5 h-3.5 text-green-500 mr-2" />
                        <span className="text-gray-700">Driver Charges</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <CheckCircle className="w-3.5 h-3.5 text-green-500 mr-2" />
                        <span className="text-gray-700">Fuel Cost</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <CheckCircle className="w-3.5 h-3.5 text-green-500 mr-2" />
                        <span className="text-gray-700">Parking Charges</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <CheckCircle className="w-3.5 h-3.5 text-green-500 mr-2" />
                        <span className="text-gray-700">Vehicle Insurance</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <CheckCircle className="w-3.5 h-3.5 text-green-500 mr-2" />
                        <span className="text-gray-700">Toll Taxes</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};


const BookingForm = ({ vehicle, onBack }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    pickupLocation: '',
    dropLocation: '',
    pickupDate: '',
    pickupTime: '',
    dropDate: '',
    dropTime: '',
    tripType: 'oneway',
    passengers: '',
    specialRequests: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Phone must be 10 digits';
    }
    if (!formData.pickupLocation.trim()) newErrors.pickupLocation = 'Pickup location is required';
    if (!formData.dropLocation.trim()) newErrors.dropLocation = 'Drop location is required';
    if (!formData.pickupDate) newErrors.pickupDate = 'Pickup date is required';
    if (!formData.pickupTime) newErrors.pickupTime = 'Pickup time is required';
    
    if (formData.tripType === 'roundtrip') {
      if (!formData.dropDate) newErrors.dropDate = 'Return date is required';
      if (!formData.dropTime) newErrors.dropTime = 'Return time is required';
    }
    
    if (!formData.passengers) {
      newErrors.passengers = 'Number of passengers is required';
    } else if (parseInt(formData.passengers) > vehicle.seating) {
      newErrors.passengers = `Maximum ${vehicle.seating} passengers allowed`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


   const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };


  const sendWhatsAppMessage = () => {
    // Format the message
    const message = `*New Booking Request - Nava Cabs*

*Customer Details:*
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Passengers: ${formData.passengers}

*Trip Details:*
Vehicle: ${vehicle.name} (${vehicle.seating} seater)
Pickup: ${formData.pickupLocation}
Drop: ${formData.dropLocation}
Trip Type: ${formData.tripType === 'oneway' ? 'One Way' : 'Round Trip'}

*Schedule:*
Pickup Date: ${formatDate(formData.pickupDate)}
Pickup Time: ${formData.pickupTime}
${formData.tripType === 'roundtrip' ? `Return Date: ${formatDate(formData.dropDate)}\nReturn Time: ${formData.dropTime}` : ''}

*Special Requests:* ${formData.specialRequests || 'None'}

*Estimated Price:* ${vehicle.priceRange} (Contact for exact quote)

---
*Submitted via Nava Cabs Website*`;

    // Encode the message for URL
    const encodedMessage = encodeURIComponent(message);
    
    // WhatsApp number (use your business number)
    const whatsappNumber = '919916788688';
    
    // Create WhatsApp URL
    const whatsappURL = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    
    // Open WhatsApp in new tab
    window.open(whatsappURL, '_blank');
  };


   const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Send to WhatsApp
      sendWhatsAppMessage();
      
      // Also send to your backend if needed
      const bookingData = {
        ...formData,
        vehicle: vehicle.name,
        vehicleType: vehicle.type,
        priceRange: vehicle.priceRange,
        submittedAt: new Date().toISOString()
      };
      
      // You can also send to your API endpoint
      console.log('Booking Data for API:', bookingData);
      
      // Show success message
      alert('Booking request submitted successfully! Opening WhatsApp to confirm details...');
      
      // Close form after 2 seconds
      setTimeout(() => {
        setIsSubmitting(false);
        onBack();
        navigate('/');
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting booking:', error);
      alert('Error submitting booking. Please try again.');
      setIsSubmitting(false);
    }
  };
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="container mx-auto px-4">
        <button 
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-blue-600 transition-colors mb-5 text-sm"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          <span className="font-medium">Back to Vehicle Details</span>
        </button>

        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-5 md:p-6">
            <div className="mb-6">
              <h1 className="text-xl font-bold text-gray-800 mb-1">Book Your Ride</h1>
              <p className="text-gray-600 text-sm">Complete the form below to book {vehicle.name}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Personal Information */}
              <div>
                <h2 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-1">
                  <Users className="w-4 h-4 text-blue-600" />
                  Personal Information
                </h2>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 rounded-md border ${
                        errors.name ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm`}
                      placeholder="Enter your full name"
                    />
                    {errors.name && <p className="text-red-500 text-xs mt-0.5">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 rounded-md border ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm`}
                      placeholder="your@email.com"
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-0.5">{errors.email}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 rounded-md border ${
                        errors.phone ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm`}
                      placeholder="10-digit mobile number"
                    />
                    {errors.phone && <p className="text-red-500 text-xs mt-0.5">{errors.phone}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Number of Passengers *
                    </label>
                    <input
                      type="number"
                      name="passengers"
                      value={formData.passengers}
                      onChange={handleChange}
                      min="1"
                      max={vehicle.seating}
                      className={`w-full px-3 py-2 rounded-md border ${
                        errors.passengers ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm`}
                      placeholder={`Max ${vehicle.seating} passengers`}
                    />
                    {errors.passengers && <p className="text-red-500 text-xs mt-0.5">{errors.passengers}</p>}
                  </div>
                </div>
              </div>

              {/* Trip Details */}
              <div>
                <h2 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-1">
                  <MapPin className="w-4 h-4 text-blue-600" />
                  Trip Details
                </h2>
                
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Trip Type *
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, tripType: 'oneway' }))}
                      className={`py-2 px-3 rounded-md border font-medium transition-all text-sm ${
                        formData.tripType === 'oneway'
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      One Way
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, tripType: 'roundtrip' }))}
                      className={`py-2 px-3 rounded-md border font-medium transition-all text-sm ${
                        formData.tripType === 'roundtrip'
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      Round Trip
                    </button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Pickup Location *
                    </label>
                    <input
                      type="text"
                      name="pickupLocation"
                      value={formData.pickupLocation}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 rounded-md border ${
                        errors.pickupLocation ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm`}
                      placeholder="Enter pickup location"
                    />
                    {errors.pickupLocation && <p className="text-red-500 text-xs mt-0.5">{errors.pickupLocation}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Drop Location *
                    </label>
                    <input
                      type="text"
                      name="dropLocation"
                      value={formData.dropLocation}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 rounded-md border ${
                        errors.dropLocation ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm`}
                      placeholder="Enter drop location"
                    />
                    {errors.dropLocation && <p className="text-red-500 text-xs mt-0.5">{errors.dropLocation}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Pickup Date *
                    </label>
                    <input
                      type="date"
                      name="pickupDate"
                      value={formData.pickupDate}
                      onChange={handleChange}
                      min={today}
                      className={`w-full px-3 py-2 rounded-md border ${
                        errors.pickupDate ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm`}
                    />
                    {errors.pickupDate && <p className="text-red-500 text-xs mt-0.5">{errors.pickupDate}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Pickup Time *
                    </label>
                    <input
                      type="time"
                      name="pickupTime"
                      value={formData.pickupTime}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 rounded-md border ${
                        errors.pickupTime ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm`}
                    />
                    {errors.pickupTime && <p className="text-red-500 text-xs mt-0.5">{errors.pickupTime}</p>}
                  </div>

                  {formData.tripType === 'roundtrip' && (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Return Date *
                        </label>
                        <input
                          type="date"
                          name="dropDate"
                          value={formData.dropDate}
                          onChange={handleChange}
                          min={formData.pickupDate || today}
                          className={`w-full px-3 py-2 rounded-md border ${
                            errors.dropDate ? 'border-red-500' : 'border-gray-300'
                          } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm`}
                        />
                        {errors.dropDate && <p className="text-red-500 text-xs mt-0.5">{errors.dropDate}</p>}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Return Time *
                        </label>
                        <input
                          type="time"
                          name="dropTime"
                          value={formData.dropTime}
                          onChange={handleChange}
                          className={`w-full px-3 py-2 rounded-md border ${
                            errors.dropTime ? 'border-red-500' : 'border-gray-300'
                          } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm`}
                        />
                        {errors.dropTime && <p className="text-red-500 text-xs mt-0.5">{errors.dropTime}</p>}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Special Requests */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Special Requests (Optional)
                </label>
                <textarea
                  name="specialRequests"
                  value={formData.specialRequests}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-3 py-2 rounded-md border border-gray-300 focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none text-sm"
                  placeholder="Any special requirements or requests..."
                ></textarea>
              </div>

              {/* Vehicle Summary */}
              <div className="bg-blue-50 rounded-md p-4 border border-blue-200">
                <h3 className="font-semibold text-gray-800 mb-2 text-sm">Selected Vehicle</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-800 text-sm">{vehicle.name}</p>
                    <p className="text-xs text-gray-600">{vehicle.seating} Seater • {vehicle.type}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-600">Starting from</p>
                    <p className="text-base font-bold text-blue-600">{vehicle.priceRange}</p>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex flex-col sm:flex-row gap-3 pt-3">
                <button
                  type="button"
                  onClick={onBack}
                  className="flex-1 py-2.5 px-5 rounded-md border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-all text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-2.5 px-5 rounded-md bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold hover:from-blue-700 hover:to-blue-800 transition-all shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {isSubmitting ? 'Submitting...' : 'Confirm Booking'}
                </button>
              </div>

              <p className="text-xs text-gray-500 text-center">
                By submitting this form, you agree to our terms and conditions. We'll contact you shortly to confirm your booking.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Bus20SeaterBangalore;